import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {gql} from '@apollo/client';
import {apollo} from '../../../utils/apollo';
import DashboardLayout from '../../../layouts/DashboardLayout';
import Button from '../../../components/Button';
import useModalStore from '../../../store/modal';
import use2FAStatus from '../../../hooks/use2FAStatus';
import {toast} from 'react-toastify';

function SubUserCreate() {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const is2FAEnabled = use2FAStatus();
    const {setModal, getOtpValue, isOtpRequired, hideModal} = useModalStore();

    const [form, setForm] = useState({
        name: '',
        email: '',
        sub_user_with_full_merchant_permissions: false,
        sub_user_with_full_transaction_list: false,
    });
    const [submitting, setSubmitting] = useState(false);

    const handleChange = (e) => {
        const {name, value, type, checked} = e.target;
        setForm((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const CREATE_SUB_USER = gql`
        mutation CreateSubUser($input: CreateSubUserInput!) {
            createSubUser(input: $input) {
                success
                message
            }
        }
    `;

    const submitWithOtp = () => {
        setModal({
            isVisible: true,
            title: t('user_management.create_sub_user'),
            description: t('_.please_confirm_action_with_otp') || 'Please enter your 2FA code to confirm this action.',
            otpRequired: true,
            confirmText: t('_.confirm') || 'Confirm',
            onConfirm: async () => {
                const otp = getOtpValue();
                if (isOtpRequired() && !otp) {
                    toast.error(t('_.otp_required') || 'Please enter OTP to confirm action.');
                    return;
                }
                hideModal();
                await handleSubmit(otp);
            },
            onCancel: () => hideModal(),
        });
    };

    const handleSubmit = async (otp) => {
        if (submitting) return;
        if (!form.name?.trim() || !form.email?.trim()) {
            toast.error(t('_.validation_error') || 'Please fill all required fields.');
            return;
        }
        try {
            setSubmitting(true);
            const {data} = await apollo.mutate({
                mutation: CREATE_SUB_USER,
                variables: {
                    input: {
                        name: form.name.trim(),
                        email: form.email.trim(),
                        sub_user_with_full_merchant_permissions: !!form.sub_user_with_full_merchant_permissions,
                        sub_user_with_full_transaction_list: !!form.sub_user_with_full_transaction_list,
                        otp: otp,
                    }
                },
            });
            const res = data?.createSubUser;
            if (res?.success) {
                toast.success(res?.message || t('_.success'));
                navigate('/merchant/sub-users');
            } else {
                toast.error(res?.message || t('_.something_went_wrong'));
            }
        } catch (e) {
            const msg = e?.message || 'Request failed';
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    const onSubmit = async (e) => {
        try {
            e.preventDefault();
        } catch (e) {
        }
        if (is2FAEnabled?.isLoading) {
            toast.warn('Two-factor authentication status is still loading. Please try again.');
            return;
        }
        if (!is2FAEnabled?.data?.twoFactorEnabled) {
            toast.error('2FA is not enabled for your account');
            return;
        }
        submitWithOtp();
    };

    return (
        <DashboardLayout>
            <div className="max-w-3xl mx-auto">
                <div className="mb-8">
                    <h2 className="text-3xl font-extrabold text-gray-900 tracking-tight">{t('user_management.create_sub_user')}</h2>
                    <p className="text-gray-500 font-medium mt-1">{t('user_management.create_sub_user_description') || 'Create a sub-user account and define their permissions.'}</p>
                </div>

                <form onSubmit={onSubmit}
                      className="bg-white/60 backdrop-blur-md rounded-[2rem] shadow-sm border border-gray-100 p-6 sm:p-10">
                    <div className="grid grid-cols-1 gap-6">
                        <div>
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700">{t('user_management.table.name') || 'Full name'}</label>
                            <input
                                id="name"
                                name="name"
                                type="text"
                                required
                                value={form.name}
                                onChange={handleChange}
                                className="mt-1 block w-full rounded-xl border-gray-200 focus:border-cyan-600 focus:ring-cyan-600"
                                placeholder={t('user_management.name_placeholder') || "John Doe"}
                            />
                        </div>
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700">{t('user_management.table.email') || 'Email'}</label>
                            <input
                                id="email"
                                name="email"
                                type="email"
                                required
                                value={form.email}
                                onChange={handleChange}
                                className="mt-1 block w-full rounded-xl border-gray-200 focus:border-cyan-600 focus:ring-cyan-600"
                                placeholder={t('user_management.email_placeholder') || "john@example.com"}
                            />
                            <p className="text-xs text-gray-500 mt-2">{t('user_management.email_uniqueness_note') || 'The email must be unique and not used by any user or sub-user.'}</p>
                        </div>
                        <div className="pt-2">
                            <div className="flex items-start gap-3">
                                <input
                                    id="sub_user_with_full_merchant_permissions"
                                    name="sub_user_with_full_merchant_permissions"
                                    type="checkbox"
                                    checked={form.sub_user_with_full_merchant_permissions}
                                    onChange={handleChange}
                                    className="mt-1 h-5 w-5 rounded border-gray-300 text-cyan-600 focus:ring-cyan-600"
                                />
                                <div>
                                    <label htmlFor="sub_user_with_full_merchant_permissions"
                                           className="text-sm font-medium text-gray-900">{t('user_management.full_merchant_permissions')}</label>
                                    <p className="text-sm text-gray-500">{t('user_management.full_merchant_permissions_desc')}</p>
                                </div>
                            </div>
                        </div>
                        <div>
                            <div className="flex items-start gap-3">
                                <input
                                    id="sub_user_with_full_transaction_list"
                                    name="sub_user_with_full_transaction_list"
                                    type="checkbox"
                                    checked={form.sub_user_with_full_transaction_list}
                                    onChange={handleChange}
                                    className="mt-1 h-5 w-5 rounded border-gray-300 text-cyan-600 focus:ring-cyan-600"
                                />
                                <div>
                                    <label htmlFor="sub_user_with_full_transaction_list"
                                           className="text-sm font-medium text-gray-900">{t('user_management.full_transaction_list')}</label>
                                    <p className="text-sm text-gray-500">{t('user_management.full_transaction_list_desc')}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-8 flex items-center gap-4">
                        <Button type="submit" className="rounded-2xl px-8" disabled={submitting} onClick={onSubmit}>
                            {submitting ? t('_.please_wait') || 'Please wait...' : t('_.create') || 'Create'}
                        </Button>
                        <Button type="button" secondary={true} className="rounded-2xl px-8"
                                onClick={() => navigate('/merchant/sub-users')}>
                            {t('_.cancel') || 'Cancel'}
                        </Button>
                    </div>
                </form>
            </div>
        </DashboardLayout>
    );
}

export default SubUserCreate;